import DebugBox from "components/shareable/DebugBox";
import dayjs from "dayjs";
import { whiteOrBlackTextColor } from "helpers/StyleUtils";
import useWebsiteBuilder, { TourStyle, WebsiteBuilderContent, WebsiteBuilderStyle } from "Hooks/useWebsiteBuilder";
import { httpsLink, makeKey, styx } from "modules/Utils";
import { CSSProperties } from "react";

interface TourPreviewContentProps {
    contentIndex: number;
    websiteContent: WebsiteBuilderContent[];
    websiteStyle: WebsiteBuilderStyle;
    style?: CSSProperties;
    onClickContentBlock?: (index: number) => void;
}

const TourPreviewContent = ({
    contentIndex,
    websiteContent: websiteContentProp,
    websiteStyle: websiteStyleProp,
    style: styleProp,
    onClickContentBlock,
}: TourPreviewContentProps) => {
    const {
        websiteContent: websiteContentState,
        websiteStyle: websiteStyleState,
    } = useWebsiteBuilder();

    const websiteContent = websiteContentProp || websiteContentState;
    const websiteStyle = websiteStyleProp || websiteStyleState;

    const {
        textColor: OLD,
        secondaryColor: secondaryColorWebsiteStyle,
        backgroundColor: backgroundColorWebsiteStyle,
    } = websiteStyle;

    const isContentBackgroundColorEnabled = websiteContent[contentIndex].isContentBackgroundColorEnabled;
    const contentBackgroundColor = websiteContent[contentIndex].contentBackgroundColor;
    const backgroundColor = isContentBackgroundColorEnabled ? contentBackgroundColor || backgroundColorWebsiteStyle : backgroundColorWebsiteStyle;

    const isContentTextColorEnabled = websiteContent[contentIndex].isContentTextColorEnabled;
    const contentTextColor = websiteContent[contentIndex].contentTextColor;

    const isContentTextSecondaryColorEnabled = websiteContent[contentIndex].isContentTextSecondaryColorEnabled;
    const contentTextSecondaryColor = websiteContent[contentIndex].contentTextSecondaryColor

    const textColor = isContentTextColorEnabled ? contentTextColor : whiteOrBlackTextColor(backgroundColor);
    
    const isSecondaryColorEnabled = websiteContent[contentIndex].isContentSecondaryColorEnabled;
    const secondaryColor = isSecondaryColorEnabled ? websiteContent[contentIndex].contentSecondaryColor || '#FFFFFF' : secondaryColorWebsiteStyle;
    
    const secondaryTextColor = isContentTextSecondaryColorEnabled ? contentTextSecondaryColor : whiteOrBlackTextColor(secondaryColor);

    const tourContent = websiteContent[contentIndex].tourContent;
    const {
        tourDates,
        style
    } = tourContent || {};

    if (!tourDates) {
        return (
            <div style={{ textAlign: 'center' }}>
                No tour dates
            </div>
        )
    }

    const getTicketUrl = (ticketUrl: string, isSoldOut: boolean, isPreSalePeriod: boolean) => {
        if (!ticketUrl || isPreSalePeriod) return '';

        let url = ticketUrl;
        if (!/^https?:\/\//i.test(url)) {
            url = `https://${url.replace(/^\/+/, '')}`;
        }
        return isSoldOut || !isPreSalePeriod ? url : '';
    }

    const getTourButtonText = ({ isSoldOut, isPreSalePeriod, preSaleButtonText, showIsPassed, buttonLinkText }: { isSoldOut: boolean, isPreSalePeriod: boolean, preSaleButtonText: string, showIsPassed: boolean, buttonLinkText: string }) => {
        if (showIsPassed) {
            return 'Done';
        }
        if (isSoldOut) {
            return 'Sold Out';
        }
        if (isPreSalePeriod) {
            if (preSaleButtonText) {
                return preSaleButtonText
            }

            return 'Pre-Sale'
        }

        return buttonLinkText || 'Buy';
    }

    const handleTicketClick = (ticketUrl: string, isSoldOut: boolean, isPreSalePeriod: boolean) => {
        const url = getTicketUrl(ticketUrl, isSoldOut, isPreSalePeriod);
        const urlLocal = httpsLink(url);
        if (urlLocal) {
            window.open(urlLocal, '_blank');
        }
    }

    const renderLocationDetails = (city: string, region: string, country: string) => {
        const locationParts = [
            city,
            region,
            !['US', 'USA', 'United States'].includes(country) ? country : null
        ].filter(Boolean);
        return locationParts.join(', ');
    };

    const renderTourDates = () => {
        return tourDates.map((tourDate, index) => {
            const {
                date,
                venue,
                city,
                region,
                country,
                isSoldOut,
                isPreSalePeriod,
                preSaleButtonText,
                buttonLinkText,
                ticketUrl,
            } = tourDate;

            const today = dayjs().startOf('day');
            const eventDate = dayjs(date).startOf('day');
            const showIsPassed = eventDate.isBefore(today);

            const day = eventDate.format('DD')
            const month = eventDate.format('MMM')

            const buttonStyle = {
                backgroundColor: secondaryColor,
                color: secondaryTextColor,
                opacity: isSoldOut ? 0.5 : 1,
            };

            const locationDetails = renderLocationDetails(city, region, country);

            const firstLine = venue || locationDetails;
            const secondLine = venue ? locationDetails : null;

            switch (style) {
                case TourStyle.BOXES:
                    return (
                        <div
                            key={makeKey('tourDateRowBoxes', index, contentIndex)}
                            className="tourDateRow"
                            style={styx(
                                sty.tourDateRow,
                                { color: textColor, opacity: showIsPassed ? 0.4 : 1 }
                            )}
                        >
                            <div className="tourDatePreviewContainer" style={sty.tourDatePreviewContainer}>
                                <div className="tourDateMonth" style={sty.tourDateMonth}>
                                    {month.toUpperCase()}
                                </div>
                                <div className="tourDateDay" style={sty.tourDateDay}>
                                    {day}
                                </div>
                            </div>
                            <div className="tourDateDetailContainer" style={sty.tourDateDetailContainer}>
                                <div className="tourDateCity" style={sty.tourDateCity}>
                                    {firstLine}
                                </div>
                                {secondLine && (
                                    <div className="tourDatePlace" style={sty.tourDatePlace}>
                                        {secondLine}
                                    </div>
                                )}
                            </div>
                            <button
                                className="tourDateBuyButton"
                                style={styx(sty.tourDateBuyButton, buttonStyle)}
                                onClick={() => handleTicketClick(ticketUrl, Boolean(isSoldOut), Boolean(isPreSalePeriod))}
                            >
                                {getTourButtonText({
                                    isSoldOut: Boolean(isSoldOut),
                                    isPreSalePeriod: Boolean(isPreSalePeriod),
                                    preSaleButtonText: preSaleButtonText || '',
                                    showIsPassed,
                                    buttonLinkText: buttonLinkText || '',
                                })}
                            </button>
                        </div>
                    )
                case TourStyle.STACKED:
                    return (
                        <div
                            key={makeKey('tourDateRowStacked', index, contentIndex)}
                            className="tourDateRowStackedPreview"
                            style={styx(
                                sty.tourDateRowStackedPreview,
                                {
                                    color: textColor,
                                    opacity: showIsPassed ? 0.4 : 1
                                }
                            )}
                        >
                            <div className="tourDateDetailStackedContainer" style={sty.tourDateDetailStackedContainer}>
                                <div className={`tourDatePreviewStackedContainer ${sty.tourDatePreviewStackedContainer}`}>
                                    <div className="tourDateDay" style={sty.tourDateDay}>
                                        {month} {day.replace(/^0/, '')}
                                    </div>
                                </div>
                                <div className="tourStackedFirstLine" style={sty.tourStackedFirstLine}>
                                    {firstLine}
                                </div>
                                {secondLine &&
                                    <div
                                        className="tourStackedSecondLine"
                                        style={sty.tourStackedSecondLine}
                                    >
                                        {secondLine}
                                    </div>
                                }
                            </div>
                            <button
                                className="tourDateBuyButtonStacked"
                                style={styx(sty.tourDateBuyButtonStacked, buttonStyle)}
                                onClick={() => handleTicketClick(ticketUrl, Boolean(isSoldOut), Boolean(isPreSalePeriod))}
                            >
                                {getTourButtonText({
                                    isSoldOut: Boolean(isSoldOut),
                                    isPreSalePeriod: Boolean(isPreSalePeriod),
                                    preSaleButtonText: preSaleButtonText || '',
                                    showIsPassed,
                                    buttonLinkText: buttonLinkText || '',
                                })}
                            </button>
                        </div>
                    )
                default:
                    return (
                        <div
                            key={makeKey('tourDateRowPreview', index, contentIndex)}
                            className="nothingTourState"
                        >
                            NOTHING
                        </div>
                    )
            }
        })
    }

    // NOTE: render()
    return (
        <div
            className="tourPreviewContentComponent"
            style={styx(
                sty.tourPreviewContentComponent,
                styleProp,
            )}
            onClick={() => onClickContentBlock?.(contentIndex)}
        >
            {renderTourDates()}
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    tourPreviewContentComponent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        paddingLeft: 16,
        paddingRight: 16,
    },
    tourDateRowStackedPreview: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        marginBottom: 16,
        fontSize: 14,
    },
    tourDateDetailStackedContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
    },
    tourDatePreviewStackedContainer: {
        display: 'flex',
        gap: 8,
        justifyContent: 'center',
        alignItems: 'center',
    },
    tourStackedSecondLine: {
        opacity: 0.5,
    },





    tourDateRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 8,
        borderRadius: 8,
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        padding: 8,
    },
    tourDatePreviewContainer: {
        paddingLeft: 8,
        paddingRight: 16,
        borderRight: '1px solid rgba(255, 255, 255, 0.2)',
    },
    tourDateDay: {
        fontSize: 14,
    },
    tourDateMonth: {
        fontSize: 11,
    },
    tourDateCity: {
        fontSize: 15,
    },
    tourDatePlace: {
        fontSize: 12,
    },
    stackedTourDateText: {
        fontSize: 16,
    },
    tourDateBuyButton: {
        fontSize: 15,
        padding: '4px 16px',
        borderRadius: 100,
        whiteSpace: 'nowrap',
        maxWidth: 120,
        // border: '1px solid rgba(255, 255, 255, 0.75)',
    },
    tourDateDetailContainer: {
        marginRight: 'auto',
        textAlign: 'left',
        paddingRight: 5
    },

    tourDateBuyButtonStacked: {
        fontSize: 16,
        padding: '8px 24px',
        borderRadius: 100,
        whiteSpace: 'nowrap',
        maxWidth: 120,
        marginLeft: 'auto',
        marginRight: 'auto',
        width: '100%',
    },
}

export default TourPreviewContent;